/**
 * SwitchToFosButton — Cross-domain nav entry point (PLATFORM-003.2).
 *
 * Renders a header-bar button that mints a one-time cross-domain authorization
 * via identity-service and redirects to founderyos.dev, landing the user already
 * authenticated (via the exchange flow in PLATFORM-003.1 + 003.3).
 *
 * Cross-domain handoff (dac-001-5): `@hello-world-dao/auth@^2.2.0`'s
 * `navigateCrossDomain` generates the PKCE verifier client-side and calls
 * `POST {baseUrl}/cross-domain/authorize` (Bearer identity JWT) on
 * **identity-service**. This is the NATIVE path on the DAO-side identity host —
 * the `/api/v1/auth/*` prefix is the FOS-edge convention only and is stripped
 * FOS-side, so the installed auth package composes the bare native route here.
 * `baseUrl` is therefore the identity-service edge base
 * (`VITE_IDENTITY_SERVICE_URL`, via {@link getIdentityServiceUrl}) — the SAME
 * base the suite uses for its other `/api/v1/auth/*`-adjacent identity-service
 * calls (token exchange/refresh/logout) — NOT `VITE_ORACLE_BRIDGE_URL`, which it
 * pointed at before the Phase 2 rewrite (ADR-025 consolidation).
 *
 * Hidden when the user isn't logged in. On `session-expired` errors, redirects
 * straight to the FounderyOS dashboard, which handles authentication on arrival.
 * (The legacy think-tank login host is dead — Think Tank is deprecated, unified
 * under FounderyOS — so the fallback no longer routes there.)
 *
 * @module components/layout/SwitchToFosButton
 */

import { useState, useRef } from 'react';
import { ExternalLink } from 'lucide-react';
import {
  navigateCrossDomain,
  CrossDomainAuthError,
  useAuth,
} from '@hello-world-dao/auth';
import { getIdentityJwt } from '../../services/identity-jwt-store';
import { getIdentityServiceUrl } from '../../services/identity-service-client';

/**
 * The cross-domain SSO RECEIVER route on the FOS dashboard (dac-001-5 Phase 4).
 *
 * The browser MUST land HERE — not on a normal app route. `navigateCrossDomain`
 * builds the redirect as `https://<host><path>?code=<code>` (the `path` is the
 * `return_path` it POSTs to identity-service `/cross-domain/authorize`, used
 * VERBATIM by the server to build the redirect), then appends the PKCE verifier
 * in the `#v=` fragment. ONLY the dashboard's `/auth/cross-domain-login` page
 * mounts the PKCE receiver that exchanges `{ code, verifier }` for a FOS session.
 *
 * Landing on any other route (e.g. `/dashboard`) means NO receiver runs: the code
 * is never exchanged, the dashboard's ProtectedRoute resolves an unauthenticated
 * session and bounces to the login front-door. This was the live-staging break —
 * the button passed `targetPath` defaulting to `/dashboard`, so the redirect went
 * to `https://dashboard.staging.founderyos.dev/dashboard?code=…#v=…` (authorize
 * 200, NO `/api/v1/auth/token/exchange`, `/api/v1/auth/session` 401 → /login).
 *
 * The receiver defaults its post-exchange landing to `/` (the FOS CommandCenter /
 * dashboard home; `/dashboard` itself just redirects to `/`), which is the correct
 * "Switch to FOS" destination — so no `returnTo` needs to ride the handoff.
 */
const FOS_CROSS_DOMAIN_RECEIVER_PATH = '/auth/cross-domain-login';

/**
 * Bare target HOST for the FOS cross-domain handoff (`navigateCrossDomain`
 * builds `https://<host>/auth/cross-domain-login?...`).
 *
 * DERIVED from the single FOS-dashboard-URL source of truth (`resolveFosDashboardUrl`,
 * `VITE_FOS_DASHBOARD_URL`) so the cross-domain redirect target and the
 * session-expired fallback can NEVER drift. They previously drifted: the
 * primary path read a separate `VITE_FOUNDERYOS_DOMAIN` that pointed at the
 * DEAD `staging.founderyos.dev` monolith, while the fallback URL was correct —
 * so "Switch to FOS" sent members to the dead host.
 */
function resolveFosDomain(): string {
  try {
    return new URL(resolveFosDashboardUrl()).host;
  } catch {
    return 'dashboard.staging.founderyos.dev';
  }
}

/**
 * Absolute URL of the FounderyOS dashboard, used by the `session-expired`
 * fallback. The dashboard handles authentication on arrival, so we send the
 * member straight there (no `/login`, no `return_origin`, no dead think-tank
 * host). Derived from `VITE_FOS_DASHBOARD_URL` with a staging default; any
 * trailing slash is trimmed so the value composes cleanly.
 */
function resolveFosDashboardUrl(): string {
  const env = import.meta.env.VITE_FOS_DASHBOARD_URL;
  const base =
    typeof env === 'string' && env.length > 0
      ? env
      : 'https://dashboard.staging.founderyos.dev';
  return base.replace(/\/+$/, '');
}

export interface SwitchToFosButtonProps {
  /** Optional className for layout fit-in. */
  className?: string;
}

export function SwitchToFosButton({ className }: SwitchToFosButtonProps) {
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // BL-371: synchronous in-flight guard — prevents a double-click (or any
  // re-entrant call) from issuing a second cross-domain mint before the first
  // navigation completes. `useState` is insufficient here because React batches
  // state updates asynchronously; the `disabled` prop derived from `loading`
  // may not reach the DOM before the second click is processed. A ref is set
  // synchronously at call-entry, so concurrent invocations see it immediately.
  const inFlightRef = useRef(false);

  if (!isAuthenticated) {
    return null;
  }

  async function onClick() {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setLoading(true);
    setErrorMsg(null);
    try {
      await navigateCrossDomain(resolveFosDomain(), FOS_CROSS_DOMAIN_RECEIVER_PATH, {
        // dac-001-5: `baseUrl` is the identity-service EDGE base. The installed
        // auth package composes the NATIVE path `{baseUrl}/cross-domain/authorize`
        // (the DAO-side identity host serves native routes; the `/api/v1/auth/*`
        // strip is FOS-side only), NOT oracle-bridge. Use the
        // same accessor (`getIdentityServiceUrl` → `VITE_IDENTITY_SERVICE_URL`)
        // the suite already uses for token exchange/refresh/logout so the
        // authorize call and the rest of the identity-service traffic never drift.
        baseUrl: getIdentityServiceUrl(),
        entryProduct: 'fos',
        // Unified-identity members (ADR-015/ADR-024) authenticate via the
        // identity-service in-memory JWT + httpOnly hwdao_refresh cookie and have
        // NO legacy oracle-bridge access_token session cookie. Pass the JWT so the
        // cross-domain mint authorizes via its requireSessionAuth JWKS path
        // (uid-001-wsb-1); without it the cookie-only mint 401s and the member
        // lands on the FOS dashboard logged-out. Mirrors memberAuthenticatedFetch.
        getAccessToken: () => getIdentityJwt(),
      });
      // On success, the browser navigates away — control never returns.
    } catch (err) {
      inFlightRef.current = false;
      setLoading(false);
      if (err instanceof CrossDomainAuthError && err.code === 'session-expired') {
        // Send the member straight to the FounderyOS dashboard, which handles
        // authentication on arrival. (No /login, no return_origin, no dead
        // think-tank host.)
        window.location.href = resolveFosDashboardUrl();
        return;
      }
      const msg =
        err instanceof Error ? err.message : 'Could not switch to FounderyOS';
      setErrorMsg(msg);
    }
  }

  return (
    <div className={className}>
      <button
        type="button"
        onClick={onClick}
        disabled={loading}
        data-testid="switch-to-fos-button"
        className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
      >
        <ExternalLink className="h-4 w-4" />
        {loading ? 'Switching...' : 'Switch to FOS'}
      </button>
      {errorMsg && (
        <p
          role="alert"
          className="mt-1 text-xs text-destructive"
          data-testid="switch-to-fos-error"
        >
          {errorMsg}
        </p>
      )}
    </div>
  );
}
